/* Play along: count-in, backing track, and per-bar chord detection from the mic. */
import { useEffect, useRef, useState } from 'react';
import { useGo } from '../../app/navigation';
import type { PreparedSong } from '../../content/songs';
import { type AudioEvent, audio, type Player } from '../../core/audio';
import type { ChordRef } from '../../core/dsp';
import { isMicOn, matchChordFrame, onMic, useMicState } from '../../core/mic';
import { addXp, award, getSettings, setSetting, useSettings } from '../../core/store';
import { chordSymbol, parseChord, pretty } from '../../core/theory';
import { Btn } from '../../ui/Button';
import { ChordDiagram } from '../../ui/ChordDiagram';
import { Icon } from '../../ui/icons';
import { modStyle } from '../../ui/style';
import { MAJMIN } from '../akor/vocab';
import { MicBlock } from '../exercise/MicBlock';
import { arrange } from './arrange';
import { loadSongState, saveSongState } from './state';

type Backing = 'drums' | 'bass' | 'full';

/** Detected chord counts as the wanted one if it is the same or a close variant (7, m7, maj7). */
function sameChord(a: ChordRef, b: ChordRef): boolean {
  if (a.root !== b.root) return false;
  return (
    a.q === b.q ||
    (a.q === '7' && b.q === '') ||
    (a.q === '' && b.q === '7') ||
    (a.q === 'm7' && b.q === 'm') ||
    (a.q === 'm' && b.q === 'm7') ||
    (a.q === 'maj7' && b.q === '')
  );
}

export function PlayAlong({ song }: { song: PreparedSong }) {
  const go = useGo();
  const mic = useMicState();
  const { headphones } = useSettings();
  const [backing, setBacking] = useState<Backing>(() => (getSettings().headphones ? 'full' : 'drums'));
  // Everything here plays without pausing the microphone, so through speakers the bass and the guitar would
  // be heard as the learner's chords: they need headphones, drums never read as a chord.
  const eff: Backing = headphones ? backing : 'drums';
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [cur, setCur] = useState(-1);
  const [count, setCount] = useState<number | null>(null);
  const [beat, setBeat] = useState(-1);
  const [marks, setMarks] = useState<('hit' | 'miss' | null)[]>(() => song.bars.map(() => null));
  const [live, setLive] = useState('');
  const [result, setResult] = useState<{ pct: number } | 'nomic' | null>(null);
  const player = useRef<Player | null>(null);
  const offChord = useRef<(() => void) | null>(null);
  const curRef = useRef(-1);
  const hits = useRef<number[]>([]);
  const frames = useRef<number[]>([]);
  const barEls = useRef<(HTMLDivElement | null)[]>([]);
  const resultEl = useRef<HTMLDivElement>(null);

  const vocab = (() => {
    const set = new Map<string, ChordRef>();
    for (const c of MAJMIN) set.set(`${c.root}|${c.q}`, c);
    for (const b of song.bars) {
      const c = parseChord(b.chord);
      if (c) set.set(`${c.root}|${c.q}`, { root: c.root, q: c.q });
    }
    return Array.from(set.values());
  })();

  const finalizeBar = (i: number) => {
    if (!isMicOn() || i < 0) return;
    const ok = (frames.current[i] ?? 0) >= 3 && (hits.current[i] ?? 0) / (frames.current[i] || 1) >= 0.35;
    setMarks((m) => m.map((x, k) => (k === i ? (ok ? 'hit' : 'miss') : x)));
  };

  const stop = () => {
    setRunning(false);
    player.current?.stop();
    player.current = null;
    offChord.current?.();
    offChord.current = null;
    setCount(null);
  };

  const finish = () => {
    finalizeBar(curRef.current);
    stop();
    if (!isMicOn()) {
      setResult('nomic');
      return;
    }
    // finalizeBar's state update is async; count from the refs directly
    let scored = 0;
    song.bars.forEach((_, i) => {
      if ((frames.current[i] ?? 0) >= 3 && (hits.current[i] ?? 0) / (frames.current[i] || 1) >= 0.35) scored++;
    });
    const pct = Math.round((100 * scored) / song.bars.length);
    const st = loadSongState(song.id);
    if (pct > (st.best || 0)) saveSongState(song.id, { ...st, best: pct });
    addXp(Math.round(pct / 4), 'akor');
    if (pct >= 70) award('birlikte');
    setResult({ pct });
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  const setBar = (i: number) => {
    const prev = curRef.current;
    curRef.current = i;
    setCur(i);
    if (prev >= 0) finalizeBar(prev);
    barEls.current[i]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const start = async () => {
    if (running) {
      stop();
      return;
    }
    await audio.resume();
    setRunning(true);
    setStarted(true);
    setResult(null);
    hits.current = song.bars.map(() => 0);
    frames.current = song.bars.map(() => 0);
    curRef.current = -1;
    setCur(-1);
    setMarks(song.bars.map(() => null));
    const beatSec = 60 / song.bpm;
    const ci = audio.countInEvents(song.beats, song.bpm);
    const arr = arrange(song, {
      bass: eff !== 'drums',
      drums: true,
      chords: eff === 'full',
      melody: false,
      noGate: true,
      chordVel: 0.8,
    });
    const shift = ci.length;
    const ev: AudioEvent[] = [...ci.ev, ...arr.ev.map((e) => ({ ...e, t: e.t + shift }))];
    for (let b = 0; b < song.bars.length; b++)
      for (let k = 0; k < song.beats; k++)
        ev.push({ t: shift + (b * song.beats + k) * beatSec, cue: { type: 'beat', k } });
    setCount(song.beats);
    player.current = audio.play(ev, {
      length: shift + arr.length,
      noGate: true,
      onCue: (cue) => {
        if (cue.type === 'count') setCount(cue.n);
        else if (cue.type === 'bar') {
          setCount(null);
          setBar(cue.index);
        } else if (cue.type === 'beat') setBeat(cue.k);
      },
      onEnd: (stopped) => {
        if (!stopped) finishRef.current();
      },
    });
    if (isMicOn()) {
      offChord.current = onMic('chord', (c) => {
        const i = curRef.current;
        if (i < 0 || !c.active) return;
        const top = matchChordFrame(c, vocab)[0];
        const want = parseChord(song.bars[i]?.chord ?? '');
        if (!top || !want) return;
        frames.current[i] = (frames.current[i] ?? 0) + 1;
        if (sameChord(top, want) && top.score > 0.5) hits.current[i] = (hits.current[i] ?? 0) + 1;
        setLive(`Duyulan: ${pretty(chordSymbol(top.root, top.q))}`);
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: stop playback on unmount only
  useEffect(() => () => stop(), []);
  // the result sits under the whole chart: show it when the song ends
  useEffect(() => {
    if (result) resultEl.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [result]);

  const nowBar = cur >= 0 ? song.bars[cur] : undefined;
  const nextBar = cur >= 0 ? song.bars[cur + 1] : undefined;
  let gi = 0;
  return (
    <div className="song-page" style={modStyle('akor')}>
      <button type="button" className="link-back" onClick={() => go(`akor.sarki.${song.id}`)}>
        <Icon name="left" />
        <span>{song.title}</span>
      </button>
      <div className="song-head">
        <div className="eyebrow">Birlikte çal</div>
        <h1>{song.title}</h1>
        <p>
          Bir ölçü sayım duyacaksın, sonra şarkı başlar. Akorları sırayla çal; her ölçü bitince yeşil ya da kırmızı
          işaretlenir.
        </p>
      </div>
      {mic !== 'on' ? (
        <MicBlock text="Mikrofonu açarsan her ölçüde doğru akoru çalıp çalmadığını işaretlerim. Açmazsan akorları ekrandan takip ederek çalabilirsin." />
      ) : null}
      <div className="song-controls">
        <Btn kind="primary" size="lg" icon={running ? 'stop' : 'play'} onClick={start}>
          {running ? 'Durdur' : started ? 'Yeniden başla' : 'Başla'}
        </Btn>
        <div className="pa-backing">
          <span className="cmp-label">Eşlik:</span>
          <div className="seg">
            {(
              [
                ['drums', 'Davul'],
                ['bass', 'Davul + bas'],
                ['full', 'Tam eşlik'],
              ] as [Backing, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={eff === id ? 'on' : undefined}
                disabled={id !== 'drums' && !headphones}
                onClick={() => setBacking(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="live-note">{live}</div>
      </div>
      {headphones ? null : (
        <p className="mb-note pa-phones">
          Hoparlörden çalan bas ve gitar mikrofona karışır, bu yüzden bu eşlikler yalnızca kulaklıkla açılır.{' '}
          <button type="button" className="link" onClick={() => setSetting('headphones', true)}>
            Kulaklık taktım
          </button>
        </p>
      )}
      <div className="pa-count" hidden={count == null}>
        {count ?? ''}
      </div>
      <div className="pa-stage">
        <div className="pa-now" data-chord={nowBar?.chord ?? ''} data-bar={cur}>
          <span className="pa-k">Şimdi</span>
          <div className="pa-chord">{nowBar ? pretty(nowBar.chord) : '—'}</div>
          <div className="pa-beats">
            {Array.from({ length: song.beats }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: one dot per beat
              <span key={i} className={`pa-beat${i === beat && running ? ' on' : ''}`} />
            ))}
          </div>
          <div>{nowBar ? <ChordDiagram chord={nowBar.chord} onClick={false} /> : null}</div>
        </div>
        <div className="pa-next">
          <span className="pa-k">Sıradaki</span>
          <div className="pa-chord">{cur < 0 ? '—' : nextBar ? pretty(nextBar.chord) : 'Son'}</div>
        </div>
      </div>
      <div className="chart">
        {song.sections.map((sec, si) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional and names may repeat
          <div className="chart-sec" key={si}>
            <div className="cs-head">
              <span className="cs-name">{sec.name}</span>
            </div>
            <div className="bars">
              {sec.bars.map((b) => {
                const i = gi++;
                const cls = ['bar', cur === i ? 'now' : '', marks[i] ?? ''].filter(Boolean).join(' ');
                return (
                  <div
                    key={i}
                    className={cls}
                    data-bar={i}
                    ref={(el) => {
                      barEls.current[i] = el;
                    }}
                  >
                    <span className="b-n">{i + 1}</span>
                    <span className="b-chord">{pretty(b)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="pa-result" ref={resultEl}>
        {result === 'nomic' ? (
          <div className="ex-start-card">
            <h2>Şarkı bitti</h2>
            <p>Mikrofonu açarsan her ölçüde doğru akoru çalıp çalmadığını da gösteririm.</p>
          </div>
        ) : result ? (
          <div className="ex-start-card">
            <div className="eyebrow">Sonuç</div>
            <h2>%{result.pct} ölçü isabetli</h2>
            <p>
              {result.pct >= 85
                ? 'Şarkıyı baştan sona çaldın. Tempoyu yükseltmeyi ya da tam eşlikle çalmayı dene.'
                : result.pct >= 60
                  ? 'Çok iyi! Kırmızı ölçülerdeki geçişleri ayrıca çalış.'
                  : 'Geçişleri yavaş çalışıp yeniden dene. Kırmızı ölçüler zorlandığın yerler.'}
            </p>
            <p className="mb-note">
              Algılama çevre gürültüsüne ve akorda duyarlıdır; kulaklık takarsan "Tam eşlik" seçeneğiyle daha gerçekçi
              çalabilirsin.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
